import type { StateCreator } from 'zustand'
import type { Program, MediaAsset } from '../../types/index.js'
import type { ProgramQuery } from '../../lib/api.js'

// ─── Slice ────────────────────────────────────────────────────────────────────

export interface ProgramSlice {
  // State — catalogue
  programs:          Program[]
  programsTotal:     number
  programsPage:      number
  /** Vrai tant que le serveur a des pages non chargées pour la requête courante. */
  programsHasMore:   boolean
  programsLoading:   boolean
  programsError:     string | null
  programQuery:      ProgramQuery
  selectedProgramId: string | null

  // State — séquence de mise à jour (incrémenté à chaque PROGRAMS_UPDATED WS)
  programsUpdateSeq: number

  // State — fiche détail
  programDetail:     Program | null
  programDetailLoading: boolean

  // Actions — catalogue
  setPrograms:       (programs: Program[], total: number, page: number) => void
  /** Ajoute une page suivante à la liste déjà chargée (scroll infini). */
  appendPrograms:    (programs: Program[], total: number, page: number) => void
  setProgramQuery:   (q: Partial<ProgramQuery>) => void
  setSelectedProgram:(id: string | null) => void
  setProgramsLoading:(loading: boolean) => void
  setProgramsError:  (err: string | null) => void

  // Actions — détail
  setProgramDetail:  (p: Program | null) => void
  setProgramDetailLoading: (loading: boolean) => void

  // Actions — mutations optimistes
  upsertProgram:     (p: Program) => void
  removeProgram:     (id: string) => void
  upsertMediaAsset:  (asset: MediaAsset) => void
  removeMediaAsset:  (assetId: string, programId: string) => void

  // Actions — reload catalogue
  incrementProgramsUpdateSeq: () => void

  // Sélecteurs
  selectedProgram:   () => Program | null
}

export const createProgramSlice: StateCreator<ProgramSlice, [], [], ProgramSlice> = (set, get) => ({
  // ── État initial ─────────────────────────────────────────────────────────
  programs:            [],
  programsTotal:       0,
  programsPage:        1,
  programsHasMore:     false,
  programsLoading:     false,
  programsError:       null,
  // 50 = plafond serveur/2 : une seule requête couvre la majorité des
  // catalogues, le reste arrive au scroll (cf. appendPrograms).
  // Tri de base du catalogue : la date de première diffusion, la plus proche
  // d'abord. C'est la question que le catalogue se voit poser en premier —
  // « qu'est-ce qui passe bientôt » — là où la date de création ne dit que
  // l'ordre de saisie.
  // `premiere: 'current'` : les fiches prévues demain et plus loin ne sont pas
  // dans la liste principale, elles attendent derrière la bande « Demain et
  // plus loin » du catalogue (`useUpcomingPrograms`).
  programQuery:        { sortBy: 'premiereDate', sortDir: 'desc', limit: 50, premiere: 'current' },
  selectedProgramId:   null,
  programDetail:       null,
  programDetailLoading: false,
  programsUpdateSeq:   0,

  // ── Catalogue ────────────────────────────────────────────────────────────
  setPrograms: (programs, total, page) =>
    set({
      programs,
      programsTotal:   total,
      programsPage:    page,
      programsHasMore: programs.length > 0 && programs.length < total,
    }),

  appendPrograms: (programs, total, page) =>
    set(state => {
      // Une création optimiste (upsertProgram) peut déjà avoir inséré une des
      // lignes renvoyées : on dédoublonne par id pour éviter les clés React
      // dupliquées.
      const known  = new Set(state.programs.map(p => p.id))
      const merged = [...state.programs, ...programs.filter(p => !known.has(p.id))]
      return {
        programs:        merged,
        programsTotal:   total,
        programsPage:    page,
        programsHasMore: programs.length > 0 && merged.length < total,
      }
    }),

  setProgramQuery: (q) =>
    set(state => ({
      programQuery: { ...state.programQuery, ...q },
      programsPage: 1,  // Reset page si filtre change
    })),

  setSelectedProgram:  (id)      => set({ selectedProgramId: id }),
  setProgramsLoading:  (loading) => set({ programsLoading: loading }),
  setProgramsError:    (err)     => set({ programsError: err }),

  // ── Détail ───────────────────────────────────────────────────────────────
  setProgramDetail:        (p)       => set({ programDetail: p }),
  setProgramDetailLoading: (loading) => set({ programDetailLoading: loading }),

  // ── Mutations optimistes ──────────────────────────────────────────────────
  upsertProgram: (p) =>
    set(state => {
      if (p.parentProgramId) {
        const updateParent = (root: Program) => root.id === p.parentProgramId
          ? { ...root, parts: (root.parts ?? []).map(part => part.id === p.id ? p : part) }
          : root
        return {
          programs: state.programs.map(updateParent),
          programDetail: state.programDetail?.id === p.id ? p : state.programDetail ? updateParent(state.programDetail) : null,
        }
      }
      const idx = state.programs.findIndex(x => x.id === p.id)
      const programs = idx >= 0
        ? state.programs.map(x => x.id === p.id ? p : x)
        : [p, ...state.programs]
      const programDetail = state.programDetail?.id === p.id ? p : state.programDetail
      return { programs, programDetail }
    }),

  removeProgram: (id) =>
    set(state => ({
      programs:      state.programs.filter(p => p.id !== id),
      selectedProgramId: state.selectedProgramId === id ? null : state.selectedProgramId,
      programDetail: state.programDetail?.id === id ? null : state.programDetail,
    })),

  upsertMediaAsset: (asset) =>
    set(state => {
      const updateProgram = (program: Program): Program => {
        if (program.id === asset.programId) {
          const existing = program.mediaAssets ?? []
          const idx = existing.findIndex(candidate => candidate.id === asset.id)
          return {
            ...program,
            mediaAssets: idx >= 0
              ? existing.map(candidate => candidate.id === asset.id ? asset : candidate)
              : [...existing, asset],
          }
        }
        if (program.parts?.some(part => part.id === asset.programId)) {
          return { ...program, parts: program.parts.map(updateProgram) }
        }
        return program
      }

      return {
        programDetail: state.programDetail ? updateProgram(state.programDetail) : null,
        programs: state.programs.map(updateProgram),
      }
    }),

  removeMediaAsset: (assetId, programId) =>
    set(state => {
      const updateProgram = (program: Program): Program => {
        if (program.id === programId) {
          return { ...program, mediaAssets: (program.mediaAssets ?? []).filter(asset => asset.id !== assetId) }
        }
        if (program.parts?.some(part => part.id === programId)) {
          return { ...program, parts: program.parts.map(updateProgram) }
        }
        return program
      }
      return {
        programDetail: state.programDetail ? updateProgram(state.programDetail) : null,
        programs: state.programs.map(updateProgram),
      }
    }),

  // ── Reload catalogue ──────────────────────────────────────────────────────
  incrementProgramsUpdateSeq: () => set(state => ({ programsUpdateSeq: state.programsUpdateSeq + 1 })),

  // ── Sélecteurs ────────────────────────────────────────────────────────────
  selectedProgram: () => {
    const { programs, selectedProgramId } = get()
    return programs.find(p => p.id === selectedProgramId) ?? null
  },
})
